<template>
    <div style="position: relative; min-height: 100px;">
        <!-- Toolbar -->
        <div class="d-flex align-items-center justify-content-end px-2 py-2 gap-2">
            <div
                class="dropdown"
                style="position: relative;"
            >
                <button
                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                    type="button"
                    :disabled="selectedRows.length === 0"
                    @click.stop="toggleDropdown"
                >
                    {{ $t('route_optimization.actions') }} <span v-if="selectedRows.length > 0">({{ selectedRows.length }})</span>
                </button>
                <ul
                    class="dropdown-menu"
                    :class="{ show: dropdownOpen }"
                    style="right: 0; left: auto;"
                >
                    <li>
                        <button
                            class="dropdown-item text-danger"
                            @click="confirmDeleteSelected"
                        >
                            <font-awesome-icon
                                icon="trash"
                                class="me-2"
                            />
                            {{ $t('route_optimization.delete_selected') }}
                        </button>
                    </li>
                </ul>
            </div>
        </div>

        <div
            v-if="!loading && jobs.length === 0"
            class="text-center mt-5 text-muted"
        >
            {{ $t('common.no_results') }}
        </div>

        <div
            v-if="jobs.length > 0"
            class="col-sm-12 list-content result-table"
        >
            <vue3-datatable
                ref="jobsList"
                :rows="jobs"
                :columns="fields"
                :pagination="false"
                :sticky-header="true"
                height="100%"
                :sortable="true"
                sort-column="created_at"
                sort-direction="desc"
                skin="bh-table-compact"
                row-class="routa-table-row"
                cell-class="routa-table-cell"
                :has-checkbox="true"
                @row-select="rowSelected"
            >
                <template #operation_type="data">
                    {{ $t('route_optimization.' + (data.value.operation_type || 'winter_maintenance')) }}
                </template>
                <template #status="data">
                    <span :class="getStatusBadgeClass(data.value.status)">
                        {{ $t('route_optimization.' + data.value.status) }}
                    </span>
                </template>
                <template #name="data">
                    {{ data.value.name ?? '-' }}
                </template>
                <template #created_at="row">
                    {{ getDateString(row.value.created_at) }}
                </template>
                <template #actions="data">
                    <div class="d-flex justify-content-end gap-3">
                        <span
                            v-if="data.value.status === 'SUCCEEDED' && isObserver"
                            class="nopads"
                            :title="$t('route_optimization.create_order')"
                            @click.stop="openCreateOrderModal(data.value)"
                        >
                            <font-awesome-icon
                                icon="file-circle-plus"
                                class="row-icon"
                            />
                        </span>
                        <span
                            v-if="data.value.status === 'SUCCEEDED'"
                            class="nopads"
                            :title="$t('route_optimization.view_simulation')"
                            @click.stop="loadOptimization(data.value)"
                        >
                            <font-awesome-icon
                                icon="eye"
                                class="row-icon"
                            />
                        </span>
                        <span
                            v-if="data.value.status === 'SUCCEEDED'"
                            class="nopads"
                            :title="$t('route_optimization.copy_as_new')"
                            @click.stop="copyOptimization(data.value)"
                        >
                            <font-awesome-icon
                                icon="copy"
                                class="row-icon"
                            />
                        </span>
                        <span
                            v-if="data.value.status === 'FAILED'"
                            class="nopads"
                            @click.stop="rerunOptimization(data.value)"
                        >
                            <font-awesome-icon
                                icon="redo"
                                class="row-icon"
                            />
                        </span>
                    </div>
                </template>
            </vue3-datatable>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="loading-spinner"
        />
    </div>

    <BaseModal
        v-if="showDeleteConfirm"
        :hide-footer="true"
        :title="$t('route_optimization.delete_job')"
        @close="showDeleteConfirm = false"
    >
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-body">
                    <h5 class="modal-title justify-content-center text-center">
                        {{ $t('route_optimization.delete_jobs_confirm', { count: selectedRows.length }) }}
                    </h5>
                </div>
                <div class="modal-footer justify-content-center mb-2 mt-2">
                    <button
                        type="button"
                        class="btn btn-secondary me-2"
                        style="min-width: 100px;"
                        @click="showDeleteConfirm = false"
                    >
                        {{ $t('common.no') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        style="min-width: 100px;"
                        @click="deleteSelected"
                    >
                        {{ $t('common.yes') }}
                    </button>
                </div>
            </div>
        </div>
    </BaseModal>

    <CreateOrdersFromJobModal
        v-if="showCreateOrderModal && createOrderJob"
        :job="createOrderJob"
        @close="showCreateOrderModal = false; createOrderJob = null"
        @created="onOrderCreated"
    />
</template>

<script>
import Vue3Datatable from '@bhplugin/vue3-datatable'
import BaseModal from '@/components/BaseModal.vue'
import CreateOrdersFromJobModal from './CreateOrdersFromJobModal.vue'
import { restApi } from '../mixins/RestApiMixin';
import { timeUtils } from '../mixins/TimeUtils';

export default {
    name: "RouteOptimizationJobs",
    components: { Vue3Datatable, BaseModal, CreateOrdersFromJobModal },
    mixins: [restApi, timeUtils],
    props: {
        isObserver: {
            type: Boolean,
            default: false
        }
    },
    emits: ['view-simulation', 'rerun-setup'],
    data() {
        return {
            fields: [
                { field: 'name', title: this.$t('route_optimization.name'), sort: true, headerClass: 'routa-table-header' },
                { field: 'created_at', title: this.$t('common.created_at'), sort: true, headerClass: 'routa-table-header' },
                { field: 'operation_type', title: this.$t('route_optimization.operation_type'), sort: true, headerClass: 'routa-table-header' },
                { field: 'status', title: this.$t('route_optimization.status'), sort: true, headerClass: 'routa-table-header' },
                { field: 'actions', title: '', sort: false, headerClass: 'routa-table-header', cellClass: 'text-end' },
            ],
            jobs: [],
            loading: true,
            selectedRows: [],
            showDeleteConfirm: false,
            dropdownOpen: false,
            showCreateOrderModal: false,
            createOrderJob: null,
        };
    },

    mounted() {
        this.refresh();
    },

    methods: {
        handleResponse(resp) {
            this.jobs = (resp.data || []).map(job => ({
                ...job,
                actions: ''
            }));
            this.loading = false;
        },
        onError(err) {
            this.loading = false;
        },
        refresh() {
            this.loading = true;
            this.selectedRows = [];
            this.restFetch(this.getRouteOptimizationJobsUrl, this.handleResponse, this.onError);
        },
        rowSelected(rows) {
            this.$nextTick(() => {
                this.selectedRows = this.$refs.jobsList?.selectedRows || rows || [];
                if (this.selectedRows.length === 0) {
                    this.dropdownOpen = false;
                }
            });
        },
        toggleDropdown() {
            if (this.selectedRows.length === 0) return;
            this.dropdownOpen = !this.dropdownOpen;
        },
        confirmDeleteSelected() {
            if (this.selectedRows.length === 0) return;
            this.dropdownOpen = false;
            this.showDeleteConfirm = true;
        },
        async deleteSelected() {
            this.showDeleteConfirm = false;
            this.loading = true;
            try {
                await Promise.all(
                    this.selectedRows.map(row =>
                        new Promise((resolve, reject) => {
                            this.restDelete(
                                `${this.routeOptimizationJobUrl}/${row.job_id}`,
                                resolve,
                                reject
                            );
                        })
                    )
                );
            } catch (err) {
                console.error('Failed to delete jobs:', err);
            } finally {
                this.refresh();
            }
        },
        getStatusBadgeClass(status) {
            const classes = {
                'SUCCEEDED': 'badge rounded-pill text-bg-success text-white',
                'PROCESSING': 'badge rounded-pill text-bg-primary',
                'PENDING': 'badge rounded-pill text-bg-warning',
                'FAILED': 'badge rounded-pill text-bg-danger'
            };
            return classes[status] || 'badge rounded-pill text-bg-secondary';
        },
        openCreateOrderModal(job) {
            this.createOrderJob = job;
            this.showCreateOrderModal = true;
        },
        onOrderCreated() {
            this.showCreateOrderModal = false;
            this.createOrderJob = null;
            this.refresh();
        },
        async rerunOptimization(job) {
            this.loading = true;
            this.restFetch(`${this.routeOptimizationJobUrl}/${job.job_id}`, async (resp) => {
                const data = resp.data;
                if (data?.request_data) {
                    await this.$store.dispatch('routeOptimization/loadSetupFromRequestData', {
                        requestData: data.request_data,
                        name: job.name,
                        jobId: job.job_id,
                        operationType: data.operation_type,
                    });
                    this.$emit('rerun-setup');
                }
                this.loading = false;
            }, () => { this.loading = false; });
        },
        async copyOptimization(job) {
            this.loading = true;
            this.restFetch(`${this.routeOptimizationJobUrl}/${job.job_id}`, async (resp) => {
                const data = resp.data;
                if (data?.request_data) {
                    await this.$store.dispatch('routeOptimization/copyFromJob', {
                        requestData: data.request_data,
                        name: job.name,
                        copyLabel: this.$t('route_optimization.copy'),
                        operationType: data.operation_type,
                    });
                    this.$emit('rerun-setup');
                }
                this.loading = false;
            }, () => { this.loading = false; });
        },
        async loadOptimization(job) {
            this.loading = true;
            this.restFetch(`${this.routeOptimizationJobUrl}/${job.job_id}`, async (resp) => {
                const data = resp.data;
                if (data) {
                    this.$store.dispatch('routeOptimization/processOptimizationResult', data.result_data);
                    if (data.request_data) {
                        // Restore loadedGeometries and operationType so setup reflects the job
                        // correctly when navigating back from simulation.
                        this.$store.dispatch('routeOptimization/loadSetupFromRequestData', {
                            requestData: data.request_data,
                            name: job.name,
                            jobId: job.job_id,
                            operationType: data.operation_type,
                        });
                    }
                    this.$emit('view-simulation');
                }
                this.loading = false;
            }, () => { this.loading = false; });
        }
    }
}
</script>

<style scoped>
.loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    border: .3em solid #404041;
    border-radius: 50%;
    border-top: .3em solid #7FC000;
    width: 3em;
    height: 3em;
    -webkit-animation: spin 2s linear infinite;
    animation: spin 1s linear infinite;
}
</style>
